// @flow
import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import Modal from '@localytics/loc-components/lib/Modal';
import {
  StandardNode,
  STANDARD_HEIGHT,
  STANDARD_RADIUS_X,
  STANDARD_RADIUS_Y
} from './StandardNode';
import {
  DELAY_FILL_COLOR,
  DELAY_ICON,
  EVENT_LISTENER_FILL_COLOR,
  EVENT_LISTENER_ICON,
  MESSAGE_FILL_COLOR,
  MESSAGE_ICON
} from '../constants/Style';

import {
  DELAY_TYPE,
  EVENT_LISTENER_TYPE,
  MESSAGE_TYPE
} from '../constants/ApiTypes';
import { HOUR } from '../util/TimeUtils';
import DelayNodeModal from './DelayNodeModal';
import EventNodeModal from './EventNodeModal';
import PushNodeModal from './PushNodeModal';
import { deselectNode } from '../actions/tree';
import { addNode, getOrchestrationVersionCreatives } from '../actions/api';
import { validateCreatives } from '../util/ValidationUtils';
import {
  CREATIVE_RICH_MSG_URL_HTTPS,
  CREATIVE_RICH_MSG_URL_FILETYPE
} from '../constants/ValidationTypes';
import { getQueryParams } from '../util/RouterUtils';
import './stylesheets/NodePicker.scss';
import { ORCH_ENDPOINT_PATH } from '../config';

const Row = (props: { title: string, subtitle: string }) => (
  <g>
    <text
      fill="#555555"
      style={{
        fontSize: '16px'
      }}
    >
      {props.title}
    </text>
    <text
      y="20"
      fill="#818799"
      style={{
        fontSize: '14px'
      }}
    >
      {props.subtitle}
    </text>
  </g>
);

type NodePickerProps = {
  parentName: string,
  next: string,
  nextKey: string,
  height: number,
  rx: number,
  ry: number,
  selected: boolean,
  dispatch: Function,
  appsInVersion: Array<{
    app_uuid: string,
    olap_id: number
  }>,
  nodesInVersion: Object,
  currentVersionId: number,
  currentOrchestrationId: number,
  currentOrgId: number,
  appGroupId: number,
  editable: boolean,
  apps: Object // todo: be more descriptive
};

class NodePicker extends React.Component<
  NodePickerProps,
  { creatingNodeType: ?string }
> {
  state = { creatingNodeType: null };

  submitFormCallback = nodeData => {
    const {
      parentName,
      next,
      nextKey,
      currentVersionId: versionId,
      currentOrchestrationId: orchestrationId,
      currentOrgId: orgId,
      appGroupId,
      appsInVersion,
      nodesInVersion
    } = this.props;
    this.props.dispatch(
      addNode({
        apps: appsInVersion,
        nodes: nodesInVersion,
        versionId,
        orchestrationId,
        orgId,
        appGroupId,
        parentName,
        next,
        nextKey,
        nodeData
      })
    );
    this.props.dispatch(deselectNode());
  };
  cancel = () => this.props.dispatch(deselectNode());
  close = () => this.props.dispatch(deselectNode());

  renderNewNodeModal = nodeType => {
    const nodeStyleProps = {
      height: STANDARD_HEIGHT,
      rx: STANDARD_RADIUS_X,
      ry: STANDARD_RADIUS_Y,
      editable: this.props.editable
    };
    const defaultTimeSettings = {
      duration_minutes: 60,
      unit: HOUR
    };
    const modalCallbacks = {
      submitFormCallback: this.submitFormCallback,
      cancelCallback: this.cancel,
      closeCallback: this.close
    };

    switch (nodeType) {
      case DELAY_TYPE: {
        const {
          parentName,
          currentOrchestrationId,
          currentVersionId
        } = this.props;
        const delayNodeModalProps = {
          ...defaultTimeSettings,
          ...nodeStyleProps,
          ...modalCallbacks,
          currentOrchestrationId,
          currentVersionId,
          parentName
        };
        return <DelayNodeModal {...delayNodeModalProps} />;
      }
      case EVENT_LISTENER_TYPE: {
        const {
          parentName,
          currentOrchestrationId,
          currentVersionId,
          currentOrgId
        } = this.props;
        const eventNodeModalProps = {
          ...defaultTimeSettings,
          ...nodeStyleProps,
          ...modalCallbacks,
          currentOrgId,
          currentOrchestrationId,
          currentVersionId,
          parentName,
          event_mapping_id: null
        };
        return <EventNodeModal {...eventNodeModalProps} />;
      }
      case MESSAGE_TYPE: {
        // todo: do individual props
        const {
          appsInVersion,
          apps: appMap,
          selected,
          nodesInVersion,
          parentName,
          next,
          nextKey,
          currentVersionId: versionId,
          currentOrchestrationId: orchestrationId,
          currentOrgId: orgId,
          appGroupId
        } = this.props;
        const apps = appsInVersion
          .slice()
          .sort((a, b) => {
            const { name: nameA = '' } = appMap[a.app_uuid];
            const { name: nameB = '' } = appMap[b.app_uuid];
            return nameA > nameB ? 1 : -1;
          })
          .map(appInVersion => {
            const { app_uuid: appId, olap_id } = appInVersion;
            const app = appMap[appId] || {};
            const {
              uuid = '',
              name = '',
              integration_platform = '', // eslint-disable-line
              icon: { url = '' } = {}
            } = app;

            return {
              appId: uuid,
              olapId: olap_id,
              name,
              platform: integration_platform,
              iconURL: url
            };
          });
        const creatives = apps.map(app => ({
          message_type: 'push',
          data: {
            title: '',
            body: '',
            attachment_url: '',
            attachment_type: '',
            deeplink: '',
            key_values: {}
          },
          platform: app.platform,
          olap_id: app.olapId,
          app_uuid: app.appId
        }));
        return (
          <Modal
            show={selected}
            className="PushNodeModal PushNodeModal--no-backdrop"
            onHide={this.close}
          >
            <PushNodeModal
              creatives={creatives}
              apps={apps}
              {...nodeStyleProps}
              {...modalCallbacks}
              submitFormCallback={nodeData => {
                const creativeErrors = validateCreatives(nodeData.creatives);
                if (creativeErrors[CREATIVE_RICH_MSG_URL_HTTPS]) {
                  alert(creativeErrors[CREATIVE_RICH_MSG_URL_HTTPS]);
                  return;
                } else if (creativeErrors[CREATIVE_RICH_MSG_URL_FILETYPE]) {
                  alert(creativeErrors[CREATIVE_RICH_MSG_URL_FILETYPE]);
                  return;
                }
                const promises = nodeData.creatives.map(creative => {
                  const endpoint = `${
                    ORCH_ENDPOINT_PATH
                  }v1/orchestration/organization/${orgId}/orchestration/${
                    orchestrationId
                  }/version/${versionId}/creatives`;
                  const options = {
                    credentials: 'include',
                    method: 'POST',
                    headers: {
                      Accepts: 'application/json',
                      'X-Requested-With': 'XMLHttpRequest',
                      'content-type': 'application/json'
                    },
                    body: JSON.stringify({
                      ...creative
                    })
                  };
                  return fetch(endpoint, options);
                });
                Promise.all(promises)
                  .then(results =>
                    Promise.all(results.map(res => res.json())).then(json => {
                      const creativeIds = json.map(j => j.creative.id);
                      this.props.dispatch(
                        getOrchestrationVersionCreatives(
                          orgId,
                          orchestrationId,
                          versionId
                        )
                      );
                      this.props.dispatch(
                        addNode({
                          apps: appsInVersion,
                          nodes: nodesInVersion,
                          versionId,
                          orchestrationId,
                          orgId,
                          appGroupId,
                          parentName,
                          next,
                          nextKey,
                          nodeData: {
                            node_type: 'message',
                            message_type: 'push',
                            creative_ids: creativeIds,
                            display_name: nodeData.display_name
                          }
                        })
                      );
                      this.props.dispatch(deselectNode());
                    })
                  )
                  .catch(err => {
                    console.log(err);
                  });
              }}
            />
          </Modal>
        );
      }
      default:
        return null;
    }
  };
  render() {
    return this.state.creatingNodeType ? (
      <div>{this.renderNewNodeModal(this.state.creatingNodeType)}</div>
    ) : (
      <div className="NodePicker__MainContainer">
        <svg
          className="NodePicker__RowContainer NodePicker__EventListenerRow"
          onClick={() =>
            this.setState({ creatingNodeType: EVENT_LISTENER_TYPE })
          }
        >
          <StandardNode
            icon={EVENT_LISTENER_ICON}
            iconFill={EVENT_LISTENER_FILL_COLOR}
            topLeftRounded
            botLeftRounded={false}
          >
            <Row
              title="Add event listener"
              subtitle="Select an event and how long users have to perform it"
            />
          </StandardNode>
        </svg>
        <svg
          className="NodePicker__RowContainer NodePicker__DelayRow"
          onClick={() => this.setState({ creatingNodeType: DELAY_TYPE })}
        >
          <StandardNode
            icon={DELAY_ICON}
            iconFill={DELAY_FILL_COLOR}
            topLeftRounded={false}
            botLeftRounded={false}
          >
            <Row
              title="Add wait timer"
              subtitle="Select how long to wait before users proceed to next step"
            />
          </StandardNode>
        </svg>
        <svg
          className="NodePicker__RowContainer NodePicker__MessageRow"
          onClick={() => this.setState({ creatingNodeType: MESSAGE_TYPE })}
        >
          <StandardNode
            icon={MESSAGE_ICON}
            iconFill={MESSAGE_FILL_COLOR}
            topLeftRounded={false}
            botLeftRounded
          >
            <Row
              title="Send push message"
              subtitle="Create a push message to send users"
            />
          </StandardNode>
        </svg>
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const {
    orgId: currentOrgId,
    orchestrationId: currentOrchestrationId,
    versionId: currentVersionId
  } = getQueryParams(ownProps.location);
  const { api: { versionsByOrchestrationId }, railsApi: { apps } } = state;
  const version =
    versionsByOrchestrationId[currentOrchestrationId][currentVersionId];
  return {
    appsInVersion: version.apps,
    nodesInVersion: version.nodes,
    appGroupId: version.app_group_id,
    apps,
    currentOrgId,
    currentVersionId,
    currentOrchestrationId
  };
};

export default withRouter(connect(mapStateToProps)(NodePicker));



// WEBPACK FOOTER //
// ./src/components/NodePicker.jsx